import { useContext, useMemo } from 'react';

import { AppMenuContext } from '@core-ui/components';
import { useUserAccess } from '@moniebook-web/components/shared/hooks';
import type { AppSubmodule } from '@moniebook-web/components/widgets/sidebar/types';
import { useFeatureFlagStore } from '@moniebook-web/store';
import { ApplicationModule } from '@moniebook-web/types';

import type { SidebarMenuGroup } from './sidebar/types';

const NON_CHILDREN_RAIL_ITEMS = [
    ApplicationModule.SETTINGS,
    ApplicationModule.HOME,
];

export function useAppSidebar(): SidebarMenuGroup[] {
    const { featureFlags } = useFeatureFlagStore();
    const { menu } = useContext(AppMenuContext);
    const { canAccess, loading } = useUserAccess();

    return useMemo<SidebarMenuGroup[]>(() => {
        if (!menu.length || loading) return [];

        const menuGroups = menu.flatMap((menu) => menu.modules);
        const subModules = menu.flatMap((menu) => menu.subModules);
        const moduleMap: Record<string, AppSubmodule[]> = {};

        subModules.forEach((sub) => {
            if (!sub) return;

            const {
                showInSettings,
                accessCodeList,
                grantAccessIfAny,
                featureFlag,
                parentId,
            } = sub;

            if (showInSettings) return;

            if (
                accessCodeList?.length &&
                !canAccess(accessCodeList, grantAccessIfAny)
            ) {
                return;
            }

            if (featureFlag && !featureFlags?.[featureFlag]) return;

            if (!parentId) return;

            moduleMap[parentId] ??= [];
            moduleMap[parentId].push(sub);
        });

        return menuGroups
            .filter(Boolean)
            .map((group) => ({
                ...group,
                children: moduleMap[group.id] ?? [],
            }))
            .filter((group) => {
                if (
                    NON_CHILDREN_RAIL_ITEMS.includes(
                        group.id as ApplicationModule,
                    )
                ) {
                    return true;
                }

                return group.children.length > 0;
            })
            .sort((a, b) => a.order - b.order);
    }, [menu, featureFlags, canAccess, loading]);
}
